// THE SCOPE DOCUMENT — the live register rendered in Statement-of-Work structure, editable
// where it matters while the scope is still being argued.
//
// Two modes. DRAFT is the list: requirements (scope, constraints, assumptions, exclusions) and
// deliverables — the surface for going back and forth on a proposal. FULL is every SOW section
// the renderer prints (parties, background, timeline, fees, acceptance, Annex A, signatures),
// with the same lists inside. Both read one payload: GET /contracting/register-document, which
// is buildSowContext() — so what you see here partitions exactly as the SOW will.
//
// Priority and type are inline selects on every requirement row. A change writes through
// POST /register/bulk (audited as requirement.bulk_update) and the page re-reads the context,
// so retyping a row from functional to constraint saves it AND moves it into the Constraints
// section in front of you. Rows inside a signed baseline (or descoped) are locked here for the
// same reason the server refuses them: that is change-request territory (P5).
//
// <SowDocument> is pure — context in, mode, an editable flag, an onEdit callback. The client
// portal can mount it read-only later without touching this page.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { PageHeader, PageShell, Sep, Zone, ZoneHead } from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { contractingApi, type SowDocRow, type SowDocumentContext } from '../api/contracting.js';
import { registerApi } from '../api/register.js';
import { useRouteParams } from '../routes.js';
import { useLocale, useT } from '../i18n/index.js';
import './ScopeDocumentPage.de.js';
import './ScopeDocumentPage.fr.js';

type Mode = 'draft' | 'full';
type EditField = 'priority' | 'type';

const PRIORITIES = ['must', 'should', 'could', 'wont'] as const;
const TYPES = ['functional', 'non_functional', 'constraint', 'assumption', 'out_of_scope'] as const;
const TYPE_LABEL: Record<string, string> = {
  functional: 'Functional', non_functional: 'Non-functional', constraint: 'Constraint',
  assumption: 'Assumption', out_of_scope: 'Out of scope',
};
// Mirrors CR_REQUIRED_STATUSES on the server: the bulk write refuses these, so the select
// says so up front instead of letting the operator discover it on save.
const LOCKED = new Set(['baselined', 'changed', 'descoped', 'delivered', 'accepted']);

const Ref = ({ children }: { children: ReactNode }) => <span className="src-ref">{children}</span>;
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

// ---------------------------------------------------------------------------
// The document (pure)
// ---------------------------------------------------------------------------

export interface SowDocumentProps {
  ctx: SowDocumentContext;
  mode: Mode;
  /** Render priority/type as selects. False = plain tags (portal, print). */
  editable: boolean;
  /** Row id currently being saved — its selects are disabled meanwhile. */
  savingId?: number | null;
  onEdit?: (row: SowDocRow, field: EditField, value: string) => void;
}

export function SowDocument({ ctx, mode, editable, savingId, onEdit }: SowDocumentProps) {
  const t = useT();
  const { fmtDate } = useLocale();
  const v = ctx.variables ?? {};
  const eng = ctx.engagement ?? {};
  const client = ctx.client ?? {};
  const musts = ctx.totals?.must ?? 0;
  const inScope = ctx.totals?.in_scope ?? 0;

  const cell = (row: SowDocRow, field: EditField) => {
    const value = str(row[field]);
    const label = field === 'type' ? t(TYPE_LABEL[value] ?? value) : t(value);
    const locked = LOCKED.has(str(row.status));
    if (!editable || locked) {
      return (
        <span className={`tag${field === 'priority' && value === 'must' ? ' tag-must' : ''}`}
          title={locked && editable ? t('Locked — inside a signed baseline; change it through a change request.') : undefined}>
          {label}
        </span>
      );
    }
    const options = field === 'priority' ? PRIORITIES : TYPES;
    return (
      <select
        className="form-select form-select-sm"
        value={value}
        disabled={savingId === row.id}
        aria-label={`${row.ref} · ${t(field === 'type' ? 'Type' : 'Priority')}`}
        onChange={(e) => onEdit?.(row, field, e.target.value)}
      >
        {options.map((o) => <option key={o} value={o}>{field === 'type' ? t(TYPE_LABEL[o]) : t(o)}</option>)}
      </select>
    );
  };

  const table = (rows: SowDocRow[], { showPriority = true } = {}) => rows.length ? (
    <div className="sd-tbl"><table>
      <thead><tr>
        <th className="sd-w-ref">{t('Ref')}</th>
        <th>{t('Requirement')}</th>
        {showPriority && <th className="sd-w-tag">{t('Priority')}</th>}
        <th className="sd-w-type">{t('Type')}</th>
      </tr></thead>
      <tbody>{rows.map((r) => {
        const dels = Array.isArray(r.deliverable_refs) ? (r.deliverable_refs as string[]) : [];
        return (
          <tr key={r.id} className={savingId === r.id ? 'sd-saving' : undefined}>
            <td><Ref>{r.ref}</Ref></td>
            <td>
              {str(r.statement)}
              {r.rationale ? <div className="sd-sub">{str(r.rationale)}</div> : null}
              {dels.length ? <div className="sd-sub">{t('Delivered by')} <span className="mono">{dels.join(', ')}</span></div> : null}
            </td>
            {showPriority && <td>{cell(r, 'priority')}</td>}
            <td>{cell(r, 'type')}</td>
          </tr>
        );
      })}</tbody>
    </table></div>
  ) : <p className="text-sm text-ink-3">{t('None recorded in the register.')}</p>;

  const deliverables = ctx.deliverables.length ? (
    <div className="sd-tbl"><table>
      <thead><tr><th className="sd-w-ref">{t('Ref')}</th><th>{t('Deliverable and acceptance criteria')}</th><th className="sd-w-ms">{t('Milestone')}</th></tr></thead>
      <tbody>{ctx.deliverables.map((d) => {
        const refs = Array.isArray(d.requirement_refs) ? (d.requirement_refs as string[]) : [];
        return (
          <tr key={str(d.id) || str(d.ref)}>
            <td><Ref>{str(d.ref)}</Ref></td>
            <td>
              <strong>{str(d.name)}</strong>
              {d.description ? <div className="sd-sub">{str(d.description)}</div> : null}
              {d.definition_of_done
                ? <div className="sd-sub"><strong>{t('Done when:')}</strong> {str(d.definition_of_done)}</div>
                : <div className="sd-sub text-ink-3">{t('No acceptance criteria recorded.')}</div>}
              {refs.length ? <div className="sd-sub">{t('Satisfies')} <span className="mono">{refs.join(', ')}</span></div> : null}
            </td>
            <td>{str(d.milestone_name) || '—'}</td>
          </tr>
        );
      })}</tbody>
    </table></div>
  ) : <p className="text-sm text-ink-3">{t('No deliverables are recorded for this engagement.')}</p>;

  const scopeBody = (
    <>
      <p className="sd-intro">{t('The Consultant shall deliver the requirements set out below. These requirements, and only these requirements, form the agreed baseline for this Statement of Work. {must} of {total} are classified as must.', { must: musts, total: inScope })}</p>
      {ctx.scope_groups.length
        ? ctx.scope_groups.map((g) => <div key={g.category}><h3 className="sd-h3">{g.category}</h3>{table(g.rows)}</div>)
        : <p className="text-sm text-ink-3">{t('No in-scope requirements are recorded in the register.')}</p>}
    </>
  );

  if (mode === 'draft') {
    return (
      <div className="scope-doc">
        <Zone>
          <ZoneHead label={t('Requirements')} count={inScope + ctx.constraints.length + ctx.assumptions.length + ctx.out_of_scope.length} />
          <h3 className="sd-h3 sd-h3-first">{t('Scope of work')}</h3>
          {ctx.scope_groups.map((g) => <div key={g.category}><div className="u-label sd-cat">{g.category}</div>{table(g.rows)}</div>)}
          {ctx.constraints.length ? <><h3 className="sd-h3">{t('Constraints')}</h3>{table(ctx.constraints, { showPriority: false })}</> : null}
          {ctx.assumptions.length ? <><h3 className="sd-h3">{t('Assumptions')}</h3>{table(ctx.assumptions, { showPriority: false })}</> : null}
          {ctx.out_of_scope.length ? <><h3 className="sd-h3">{t('Out of scope')}</h3>{table(ctx.out_of_scope, { showPriority: false })}</> : null}
        </Zone>
        <Zone>
          <ZoneHead label={t('Deliverables')} count={ctx.deliverables.length} />
          {deliverables}
        </Zone>
      </div>
    );
  }

  const sponsor = ctx.sponsor;
  const sections: { key: string; title: string; body: ReactNode }[] = [
    { key: 'parties', title: t('Parties and engagement'), body: (
      <table className="sd-kv"><tbody>
        <tr><td>{t('Client')}</td><td><strong>{str(client.legal_name) || str(client.name)}</strong></td></tr>
        <tr><td>{t('Consultant')}</td><td><strong>{v.supplier_signatory || t('The Consultant')}</strong>{v.supplier_signatory_title ? <><br />{v.supplier_signatory_title}</> : null}</td></tr>
        <tr><td>{t('Engagement')}</td><td>{str(eng.name)} <span className="mono">({str(eng.code)})</span></td></tr>
        <tr><td>{t('Client contact')}</td><td>{sponsor ? <>{str(sponsor.name)}{sponsor.role_title ? `, ${str(sponsor.role_title)}` : ''}</> : <span className="text-ink-3">{t('To be nominated')}</span>}</td></tr>
        <tr><td>{t('Commercial model')}</td><td>{str(eng.commercial_model).replace('_', ' ')}</td></tr>
        <tr><td>{t('Period')}</td><td>{v.start_date ? <span className="mono">{v.start_date}</span> : <span className="text-ink-3">{t('to be agreed')}</span>} — {v.end_date ? <span className="mono">{v.end_date}</span> : <span className="text-ink-3">{t('to be agreed')}</span>}</td></tr>
        <tr><td>{t('Governing law')}</td><td>{v.governing_law || <span className="text-ink-3">{t('to be agreed')}</span>}</td></tr>
      </tbody></table>
    ) },
    { key: 'background', title: t('Background and objectives'), body: v.background
      ? v.background.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)
      : <p className="text-sm text-ink-3">{t('No background has been recorded for this engagement.')}</p> },
    { key: 'scope', title: t('Scope of work'), body: scopeBody },
    { key: 'exclusions', title: t('Out of scope'), body: (
      <>
        <p className="sd-intro">{t('The following are expressly not included in this Statement of Work. They are listed because they were discussed: their absence here is deliberate, not an oversight. Bringing any of them into scope requires an approved Change Request.')}</p>
        {table(ctx.out_of_scope, { showPriority: false })}
      </>
    ) },
    { key: 'deliverables', title: t('Deliverables'), body: deliverables },
    { key: 'assumptions', title: t('Assumptions'), body: (
      <>
        <p className="sd-intro">{t('This Statement of Work is priced and scheduled on the following assumptions. If an assumption proves false, the affected work is handled under Change control.')}</p>
        {table(ctx.assumptions, { showPriority: false })}
      </>
    ) },
    { key: 'constraints', title: t('Constraints'), body: (
      <>
        <p className="sd-intro">{t('The following constraints bind the solution and were treated as fixed when scoping this work.')}</p>
        {table(ctx.constraints, { showPriority: false })}
      </>
    ) },
    { key: 'schedule', title: t('Timeline and milestones'), body: ctx.milestones.length ? (
      <div className="sd-tbl"><table>
        <thead><tr><th>{t('Milestone')}</th><th className="sd-w-date">{t('Due')}</th><th className="sd-w-tag">{t('Status')}</th></tr></thead>
        <tbody>{ctx.milestones.map((m) => (
          <tr key={str(m.id)}><td>{str(m.name)}</td><td className="mono">{m.due_date ? fmtDate(str(m.due_date)) : '—'}</td><td><span className="tag">{str(m.status)}</span></td></tr>
        ))}</tbody>
      </table></div>
    ) : <p className="text-sm text-ink-3">{t('No milestones are recorded for this engagement.')}</p> },
    { key: 'fees', title: t('Fees and payment schedule'), body: (
      <table className="sd-kv"><tbody>
        <tr><td>{t('Fee basis')}</td><td>{v.fee_basis}</td></tr>
        <tr><td>{t('Total fees')}</td><td>{v.fee_total ? <strong className="mono">{v.fee_total}</strong> : <span className="text-ink-3">{t('to be agreed')}</span>}</td></tr>
        <tr><td>{t('Invoicing')}</td><td>{v.invoicing}</td></tr>
        <tr><td>{t('Payment terms')}</td><td>{v.payment_terms}</td></tr>
        <tr><td>{t('Expenses')}</td><td>{v.expenses}</td></tr>
      </tbody></table>
    ) },
    { key: 'acceptance', title: t('Acceptance procedure'), body: (
      <p className="sd-intro">{t('Each deliverable is presented for acceptance against its definition of done and the acceptance criteria in Annex A. The Client reviews within {n} working days of issue; a deliverable not rejected in writing within that window is deemed accepted.', { n: str(eng.review_window_days) || '10' })}</p>
    ) },
    { key: 'annex', title: t('Annex A — Acceptance criteria'), body: ctx.acceptance_criteria.length ? ctx.acceptance_criteria.map((g) => (
      <div key={g.requirement_ref}>
        <h3 className="sd-h3"><Ref>{g.requirement_ref}</Ref> — {g.requirement_statement}</h3>
        <div className="sd-tbl"><table>
          <thead><tr><th className="sd-w-ref">{t('Ref')}</th><th>{t('Criterion')}</th><th className="sd-w-meas">{t('Measured by')}</th></tr></thead>
          <tbody>{g.rows.map((ac) => (
            <tr key={str(ac.id) || str(ac.ref)}><td><Ref>{str(ac.ref)}</Ref></td><td>{str(ac.statement)}</td><td className="text-ink-3">{str(ac.measurement_method) || '—'}</td></tr>
          ))}</tbody>
        </table></div>
      </div>
    )) : <p className="text-sm text-ink-3">{t('No acceptance criteria are recorded. Acceptance will be assessed against the definitions of done above.')}</p> },
    { key: 'signatures', title: t('Signatures'), body: (
      <div className="sd-sig">
        <div><div className="sd-sig-line" />{t('For the Client')}<br /><span className="text-ink-3">{t('name, title, date')}</span></div>
        <div><div className="sd-sig-line" />{t('For the Consultant')}<br /><span className="text-ink-3">{t('name, title, date')}</span></div>
      </div>
    ) },
  ];

  return (
    <div className="scope-doc">
      <div className="sd-totals">
        <div><b>{inScope}</b><span>{t('in-scope requirements')}</span></div>
        <div><b>{musts}</b><span>{t('must')}</span></div>
        <div><b>{ctx.out_of_scope.length}</b><span>{t('out of scope')}</span></div>
        <div><b>{ctx.deliverables.length}</b><span>{t('deliverables')}</span></div>
        <div><b>{ctx.acceptance_criteria.reduce((n, g) => n + g.rows.length, 0)}</b><span>{t('acceptance criteria')}</span></div>
      </div>
      {sections.map((s, i) => (
        <Zone key={s.key} id={`sd-${s.key}`}>
          <h2 className="sd-h2"><span className="sd-n">{String(i + 1).padStart(2, '0')}</span>{s.title}</h2>
          {s.body}
        </Zone>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function ScopeDocumentPage() {
  const t = useT();
  const { fmtDateTime } = useLocale();
  const { current } = useEngagement();
  const engagementId = current?.id ?? null;
  const toast = useToast();
  const routeParams = useRouteParams();

  const [mode, setMode] = useState<Mode>(routeParams.get('mode') === 'full' ? 'full' : 'draft');
  const [ctx, setCtx] = useState<SowDocumentContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!engagementId) { setCtx(null); return; }
    setLoading(true);
    try {
      setCtx(await contractingApi.registerDocument(engagementId));
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [engagementId, toast]);

  useEffect(() => { void load(); }, [load]);

  const onEdit = useCallback(async (row: SowDocRow, field: EditField, value: string) => {
    if (!engagementId || str(row[field]) === value) return;
    setSavingId(row.id);
    try {
      await registerApi.bulk(engagementId, [row.id], { [field]: value });
      const label = field === 'type' ? t(TYPE_LABEL[value] ?? value) : t(value);
      toast('ok', t('Saved {ref}: {field} → {value}', { ref: row.ref, field: t(field === 'type' ? 'Type' : 'Priority'), value: label }));
      // Re-read the context rather than patching state: a type change moves the row between
      // sections, and the server's partition is the only truth worth showing.
      await load();
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setSavingId(null);
    }
  }, [engagementId, load, t, toast]);

  const sub = useMemo(() => {
    if (!ctx) return null;
    return (
      <>
        <span>{str(ctx.client?.name)}</span><Sep />
        <span>{t('{n} requirements', { n: ctx.totals.in_scope + ctx.constraints.length + ctx.assumptions.length + ctx.out_of_scope.length })}</span><Sep />
        <span>{t('{n} deliverables', { n: ctx.deliverables.length })}</span><Sep />
        <span className="text-ink-3">{t('rendered {when}', { when: fmtDateTime(ctx.generated_at) })}</span>
      </>
    );
  }, [ctx, fmtDateTime, t]);

  if (!engagementId) {
    return (
      <PageShell>
        <PageHeader title={t('Scope document')} />
        <p className="text-ink-3">{t('Select an engagement to render its scope document.')}</p>
      </PageShell>
    );
  }

  return (
    <PageShell className="scope-doc-page">
      <style>{`
        .scope-doc .sd-tbl{overflow-x:auto;margin:6px 0 14px}
        .scope-doc table{border-collapse:collapse;width:100%;font-size:14px}
        .scope-doc th,.scope-doc td{text-align:left;vertical-align:top;padding:8px 10px;border-bottom:1px solid var(--line, #e3e0d8)}
        .scope-doc th{font-size:11.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--ink-3);font-weight:600}
        .scope-doc .sd-w-ref{width:82px}.scope-doc .sd-w-tag{width:110px}.scope-doc .sd-w-type{width:150px}.scope-doc .sd-w-ms{width:130px}.scope-doc .sd-w-date{width:100px}.scope-doc .sd-w-meas{width:150px}
        .scope-doc .sd-kv td:first-child{width:160px;color:var(--ink-3)}
        .scope-doc .sd-sub{margin-top:4px;font-size:13px;color:var(--ink-3)}
        .scope-doc .sd-sub strong{color:inherit}
        .scope-doc .sd-intro{max-width:70ch}
        .scope-doc .sd-h2{margin:36px 0 10px;font-size:20px}
        .scope-doc .sd-h2 .sd-n{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;color:var(--accent, var(--ink-3));margin-right:10px;vertical-align:2px}
        .scope-doc .sd-h3{margin:20px 0 6px;font-size:15.5px;font-weight:600}
        .scope-doc .sd-h3-first{margin-top:8px}
        .scope-doc .sd-cat{margin:14px 0 4px}
        .scope-doc .tag{display:inline-block;padding:1px 7px;border:1px solid var(--line, #e3e0d8);border-radius:3px;font-size:12px;white-space:nowrap}
        .scope-doc .tag-must{font-weight:600}
        .scope-doc .form-select-sm{font-size:13px;padding:2px 6px;min-width:0}
        .scope-doc .sd-saving{opacity:.6}
        .scope-doc .sd-totals{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin:4px 0 8px}
        .scope-doc .sd-totals div{border:1px solid var(--line, #e3e0d8);padding:10px 12px}
        .scope-doc .sd-totals b{display:block;font-size:22px;font-variant-numeric:tabular-nums;line-height:1.1}
        .scope-doc .sd-totals span{font-size:12.5px;color:var(--ink-3)}
        .scope-doc .sd-sig{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:32px;font-size:13.5px}
        .scope-doc .sd-sig-line{border-bottom:1px solid currentColor;height:44px;margin-bottom:6px}
        .scope-doc .mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.92em}
        @media (max-width:560px){.scope-doc .sd-totals{grid-template-columns:repeat(3,1fr)}.scope-doc .sd-sig{grid-template-columns:1fr}}
        @media print{.scope-doc-page .page-header .verbs,.scope-doc-page nav,.scope-doc select{display:none}.scope-doc .sd-tbl{overflow:visible}.scope-doc tr{break-inside:avoid}.scope-doc .sd-h2{break-after:avoid}}
      `}</style>
      <PageHeader
        kicker={str(ctx?.engagement?.code) || current?.code}
        title={t('Scope document')}
        sub={sub}
        verbs={(
          <div className="flex items-center gap-8">
            <div className="seg" role="tablist" aria-label={t('Mode')}>
              <button type="button" className={`btn btn-sm${mode === 'draft' ? ' btn-accent' : ''}`} role="tab" aria-selected={mode === 'draft'}
                title={t('Draft — requirements and deliverables only')} onClick={() => setMode('draft')}>{t('Draft')}</button>
              <button type="button" className={`btn btn-sm${mode === 'full' ? ' btn-accent' : ''}`} role="tab" aria-selected={mode === 'full'}
                title={t('Full — the complete Statement of Work structure')} onClick={() => setMode('full')}>{t('Full')}</button>
            </div>
            <button
              type="button" className="btn btn-sm"
              title={t('Print the traceability sheets — A4 landscape, one deliverable per sheet')}
              onClick={() => { if (engagementId) window.open(contractingApi.traceabilityHtmlUrl(engagementId), '_blank', 'noopener'); }}
            >{t('Print')}</button>
          </div>
        )}
      >
        <p className="text-sm text-ink-3" style={{ margin: '6px 0 0' }}>
          {t('Rendered from the live register — not an issued SOW. Priority and type are editable here and write straight to the register; rows inside a signed baseline are locked.')}
        </p>
      </PageHeader>

      {loading && !ctx && <p className="text-ink-3">{t('Loading the register…')}</p>}
      {ctx && <SowDocument ctx={ctx} mode={mode} editable savingId={savingId} onEdit={(row, field, value) => void onEdit(row, field, value)} />}
    </PageShell>
  );
}
